import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import StarRating from "../components/StarRating";
import ImageGallery from "../components/ImageGallery";
import RelatedProducts from "../components/RelatedProducts";
import CategorySpecs from "../components/CategorySpecs";
import { getProductImages, trackRecent } from "../lib/localStore";
import { formatMoney } from "../lib/categories";
import { ShoppingBag, Truck, Shield, RotateCcw, Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

export default function Product() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setQty(1);
    getProduct(id)
      .then((p) => {
        if (!mounted) return;
        setProduct(p);
        if (p) trackRecent(p);
      })
      .catch(() => mounted && setProduct(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <main className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 flex items-center justify-center text-neutral-500">
        <Loader2 className="animate-spin mr-2" size={18} />
        Loading product…
      </main>
    );
  }

  if (!product) {
    return (
      <main className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 text-center">
        <h1 className="font-display text-3xl font-bold">Product not found</h1>
        <Link to="/" className="text-sm mt-4 inline-block underline">
          Back to home
        </Link>
      </main>
    );
  }

  const onAdd = () => {
    addItem(product, qty);
    toast.success(`Added to cart · ${product.title}`);
  };

  return (
    <main className="mx-auto max-w-[1400px] px-5 lg:px-10 py-8" data-testid="product-page">
      <nav className="text-xs text-neutral-500 mb-6" data-testid="breadcrumb">
        <Link to="/" className="hover:text-neutral-900">Home</Link>
        <span className="mx-2">/</span>
        <Link
          to={`/category/${product.category}`}
          className="hover:text-neutral-900"
        >
          {product.categoryLabel}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-neutral-900 font-medium line-clamp-1 inline">
          {product.title}
        </span>
      </nav>

      <div className="grid grid-cols-12 gap-8 lg:gap-12">
        {/* Left: Gallery */}
        <div className="col-span-12 lg:col-span-7">
          <ImageGallery images={getProductImages(product)} alt={product.title} />
        </div>

        {/* Right: Details sticky */}
        <div className="col-span-12 lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <StarRating
              value={product.rating}
              count={product.reviewCount}
              showValue
              size={16}
            />
            <h1
              className="mt-3 font-display font-bold text-3xl lg:text-4xl tracking-tighter text-neutral-900 leading-tight"
              data-testid="product-title"
            >
              {product.title}
            </h1>
            <div
              className="mt-4 font-display font-bold text-4xl text-neutral-900"
              data-testid="product-price"
            >
              {formatMoney(product.price)}
            </div>
            <p className="mt-4 text-neutral-700 leading-relaxed text-[15px]">
              {product.description}
            </p>

            <CategorySpecs product={product} />

            {/* Quantity + Add to cart */}
            <div className="mt-8 flex items-center gap-3">
              <div className="inline-flex items-center h-12 rounded-full border border-neutral-300 overflow-hidden">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="h-full w-10 inline-flex items-center justify-center hover:bg-neutral-100 transition-colors"
                  data-testid="product-qty-decrement"
                  aria-label="Decrease quantity"
                >
                  <Minus size={16} />
                </button>
                <span
                  className="w-8 text-center text-sm font-medium"
                  data-testid="product-qty-value"
                >
                  {qty}
                </span>
                <button
                  onClick={() => setQty((q) => Math.min(99, q + 1))}
                  className="h-full w-10 inline-flex items-center justify-center hover:bg-neutral-100 transition-colors"
                  data-testid="product-qty-increment"
                  aria-label="Increase quantity"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={onAdd}
                className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-colors duration-200"
                data-testid="add-to-cart-button"
              >
                <ShoppingBag size={18} strokeWidth={1.5} />
                Add to Cart
              </button>
            </div>

            <ul className="mt-6 grid grid-cols-3 gap-3 text-[11px] text-neutral-600">
              <li className="border border-neutral-200 rounded-lg p-3 flex items-start gap-2">
                <Truck size={14} className="mt-0.5" />
                <span>Imaginary free shipping</span>
              </li>
              <li className="border border-neutral-200 rounded-lg p-3 flex items-start gap-2">
                <RotateCcw size={14} className="mt-0.5" />
                <span>Fictional 30-day returns</span>
              </li>
              <li className="border border-neutral-200 rounded-lg p-3 flex items-start gap-2">
                <Shield size={14} className="mt-0.5" />
                <span>Made-up warranty</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-16 border-t border-neutral-200 pt-10" data-testid="reviews-section">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-4">
            <h2 className="font-display font-bold text-2xl tracking-tight">
              Customer reviews
            </h2>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="font-display text-5xl font-bold text-neutral-900">
                {Number(product.rating).toFixed(1)}
              </span>
              <span className="text-sm text-neutral-500">/ 5</span>
            </div>
            <StarRating value={product.rating} size={18} />
            <p className="text-xs text-neutral-500 mt-2">
              Based on {product.reviewCount} fictional reviews.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8 space-y-6">
            {(product.reviews || []).map((r, idx) => (
              <div
                key={idx}
                className="border-b border-neutral-200 pb-6 last:border-b-0"
                data-testid={`review-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-sm font-medium text-neutral-800">
                      {r.author?.[0] || "?"}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-neutral-900">
                        {r.author}
                      </div>
                      <div className="text-[11px] text-neutral-500">{r.date}</div>
                    </div>
                  </div>
                  <StarRating value={r.rating} size={14} />
                </div>
                <p className="mt-3 text-sm text-neutral-700 leading-relaxed">
                  {r.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RelatedProducts product={product} />
    </main>
  );
}
